// V14.8 — Reusable rich Create/Edit dialog for NEWS ARTICLES with the
// block-editor (text + image blocks). Used by MPNewsPostsPage AND
// MPExerciseDashboard so the same Headline / Article Body / Author / Category
// / Location / Schedule layout appears wherever a news article is created.
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { TIMEZONES } from '../../lib/timezone';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { postsApi, mediaApi } from '../../lib/mpApi';
import { toast } from 'sonner';
import { Loader2, ImagePlus, Trash, Type, CalendarClock, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url?.startsWith('/') ? `${BACKEND_URL}${url}` : url;

// Block editor — each block is text or image
const ContentBlockEditor = ({ blocks, onChange, onImageUpload }) => {
  const updateBlock = (index, data) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], ...data };
    onChange(newBlocks);
  };
  const removeBlock = (index) => {
    if (blocks.length <= 1) return;
    onChange(blocks.filter((_, i) => i !== index));
  };
  const addImageBlock = async (afterIndex) => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const url = await onImageUpload(file);
        const newBlocks = [...blocks];
        newBlocks.splice(afterIndex + 1, 0, { type: 'image', url, caption: '' });
        onChange(newBlocks);
      } catch { toast.error('Image upload failed'); }
    };
    input.click();
  };
  const addTextBlock = (afterIndex) => {
    const newBlocks = [...blocks];
    newBlocks.splice(afterIndex + 1, 0, { type: 'text', content: '' });
    onChange(newBlocks);
  };
  return (
    <div className="space-y-2">
      {blocks.map((block, i) => (
        <div key={i} className="group relative">
          {block.type === 'text' ? (
            <div className="relative">
              <Textarea
                value={block.content}
                onChange={e => updateBlock(i, { content: e.target.value })}
                placeholder={i === 0 ? 'Lead paragraph...' : 'Continue article...'}
                rows={3}
                className="bg-muted border-input placeholder:text-muted-foreground pr-10"
              />
              {blocks.length > 1 && (
                <button onClick={() => removeBlock(i)}
                  className="absolute top-2 right-2 text-muted-foreground hover:text-red-400 transition opacity-0 group-hover:opacity-100">
                  <Trash className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ) : (
            <div className="relative border border-input rounded-lg overflow-hidden bg-muted/50">
              <img src={resolveUrl(block.url)} alt="" className="w-full max-h-64 object-cover" />
              <Input value={block.caption || ''} onChange={e => updateBlock(i, { caption: e.target.value })}
                placeholder="Caption (optional)..." className="bg-muted/80 border-0 rounded-none text-xs h-8" />
              <button onClick={() => removeBlock(i)}
                className="absolute top-2 right-2 bg-red-600/90 text-foreground p-1 rounded opacity-0 group-hover:opacity-100">
                <Trash className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="flex gap-1 mt-1 opacity-0 group-hover:opacity-100">
            <button onClick={() => addTextBlock(i)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground bg-muted px-2 py-1 rounded border border-input">
              <Type className="w-3 h-3" /> Text
            </button>
            <button onClick={() => addImageBlock(i)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground bg-muted px-2 py-1 rounded border border-input">
              <ImagePlus className="w-3 h-3" /> Image
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const blocksToPayload = (blocks) => {
  const contentParts = []; const mediaUrls = [];
  blocks.forEach(b => {
    if (b.type === 'text' && b.content) contentParts.push(b.content);
    else if (b.type === 'image' && b.url) {
      mediaUrls.push(b.url);
      contentParts.push(`[IMAGE: ${b.caption || 'Photo'}]`);
    }
  });
  return { content: contentParts.join('\n\n'), media_urls: mediaUrls };
};

const payloadToBlocks = (content, mediaUrls) => {
  if (!content) return [{ type: 'text', content: '' }];
  const parts = content.split('\n\n');
  const blocks = []; let mediaIdx = 0;
  parts.forEach(part => {
    const m = part.match(/^\[IMAGE: (.+)\]$/);
    if (m && mediaIdx < (mediaUrls || []).length) {
      blocks.push({ type: 'image', url: mediaUrls[mediaIdx], caption: m[1] === 'Photo' ? '' : m[1] });
      mediaIdx++;
    } else {
      blocks.push({ type: 'text', content: part });
    }
  });
  while (mediaIdx < (mediaUrls || []).length) {
    blocks.push({ type: 'image', url: mediaUrls[mediaIdx], caption: '' });
    mediaIdx++;
  }
  return blocks.length > 0 ? blocks : [{ type: 'text', content: '' }];
};

const emptyForm = () => ({
  exercise_id: '', platform: '', title: '', status: 'draft',
  author: { name: '', handle: '', bio: '', followers: 0 },
  engagement: { likes: 0, comments: 0, shares: 0, views: 0 },
  category: '', location: '', media_urls: [], schedule: null,
});

const NewsArticleFormDialog = ({
  open, onOpenChange, editingPost = null, exercises = [], platforms = [],
  lockedExerciseId = null, onSaved,
}) => {
  const [formData, setFormData] = useState(emptyForm());
  const [contentBlocks, setContentBlocks] = useState([{ type: 'text', content: '' }]);
  const [saving, setSaving] = useState(false);

  // Hydrate form ONLY when the dialog transitions open or the edited post changes.
  // We intentionally exclude `exercises` from deps — the parent (MPExerciseDashboard)
  // polls every 10s and recreates `[exercise]` on every render, which would otherwise
  // reset the form mid-typing (V14.8 bug fix).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    if (editingPost) {
      setFormData({
        exercise_id: editingPost.exercise_id, platform: editingPost.platform,
        title: editingPost.title || '', status: editingPost.status,
        author: editingPost.author || emptyForm().author,
        engagement: editingPost.engagement || emptyForm().engagement,
        category: editingPost.category || '', location: editingPost.location || '',
        media_urls: editingPost.media_urls || [],
        schedule: editingPost.schedule || null,
      });
      setContentBlocks(payloadToBlocks(editingPost.content, editingPost.media_urls));
    } else {
      setFormData({ ...emptyForm(), exercise_id: lockedExerciseId || exercises[0]?.exercise_id || '' });
      setContentBlocks([{ type: 'text', content: '' }]);
    }
  }, [open, editingPost?.post_id, lockedExerciseId]);

  // Filter platforms by (a) post type — News dialog only shows news platforms;
  // (b) the selected exercise's allow-list when configured.
  const selectedExObj = exercises.find(e => e.exercise_id === formData.exercise_id);
  const typeFiltered = platforms.filter(p => ['news', 'custom', 'regional'].includes(p.category));
  const formPlatforms = selectedExObj?.selected_platforms?.length > 0
    ? typeFiltered.filter(p => selectedExObj.selected_platforms.includes(p.platform_id) || selectedExObj.selected_platforms.includes(p.name))
    : typeFiltered;

  const handleImageUpload = async (file) => {
    const result = await mediaApi.upload(file);
    return result.file_url;
  };

  const handleSave = async () => {
    if (!formData.exercise_id || !formData.platform) {
      toast.error('Exercise and platform are required'); return;
    }
    const { content, media_urls } = blocksToPayload(contentBlocks);
    if (!content.trim() && !formData.title) {
      toast.error('Headline or content is required'); return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, content, media_urls, post_type: 'news' };
      if (editingPost) {
        await postsApi.update(editingPost.post_id, payload);
        toast.success('Article updated');
      } else {
        await postsApi.create(payload);
        toast.success('Article created');
      }
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const platformObj = formPlatforms.find(p => p.name === formData.platform);
  const platformColor = platformObj?.color || '#1a1a2e';
  const { content: previewContent, media_urls: previewMedia } = blocksToPayload(contentBlocks);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="mp-news-article-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-foreground">
            {editingPost ? 'Edit Article' : 'Create Article'}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
          {/* Form */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Exercise</Label>
                <Select value={formData.exercise_id} onValueChange={v => setFormData({ ...formData, exercise_id: v })} disabled={!!lockedExerciseId}>
                  <SelectTrigger className="bg-muted border-input"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{exercises.map(e => <SelectItem key={e.exercise_id} value={e.exercise_id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Platform</Label>
                <Select value={formData.platform} onValueChange={v => setFormData({ ...formData, platform: v })}>
                  <SelectTrigger className="bg-muted border-input"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{formPlatforms.map(p => <SelectItem key={p.platform_id} value={p.name}>{p.display_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Headline</Label>
              <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder="Breaking: Major headline here..."
                className="bg-muted border-input text-lg font-semibold placeholder:text-muted-foreground"
                data-testid="mp-news-title-input" />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Article Body</Label>
                <div className="flex gap-1">
                  <button onClick={() => setContentBlocks([...contentBlocks, { type: 'text', content: '' }])}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground bg-muted px-2 py-1 rounded border border-input">
                    <Type className="w-3 h-3" /> Add Text
                  </button>
                  <button onClick={async () => {
                    const input = document.createElement('input');
                    input.type = 'file'; input.accept = 'image/*';
                    input.onchange = async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const result = await mediaApi.upload(file);
                        setContentBlocks([...contentBlocks, { type: 'image', url: result.file_url, caption: '' }]);
                      } catch { toast.error('Upload failed'); }
                    };
                    input.click();
                  }}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground bg-muted px-2 py-1 rounded border border-input">
                    <ImagePlus className="w-3 h-3" /> Add Image
                  </button>
                </div>
              </div>
              <ContentBlockEditor blocks={contentBlocks} onChange={setContentBlocks} onImageUpload={handleImageUpload} />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Author</Label>
                <Input value={formData.author.name} onChange={e => setFormData({ ...formData, author: { ...formData.author, name: e.target.value } })}
                  placeholder="Author name" className="bg-muted border-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category</Label>
                <Input value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g. Politics, World" className="bg-muted border-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Location</Label>
                <Input value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g. London, UK" className="bg-muted border-input" />
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-3 border border-input space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" /> Schedule
                </Label>
                <Switch checked={formData.status === 'scheduled'}
                  onCheckedChange={v => setFormData({
                    ...formData, status: v ? 'scheduled' : 'draft',
                    schedule: v ? { scheduled: true, publish_datetime: '', timezone: 'UTC' } : null,
                  })} data-testid="mp-news-schedule-toggle" />
              </div>
              {formData.status === 'scheduled' && (
                <div className="grid grid-cols-2 gap-3">
                  <Input type="datetime-local" value={formData.schedule?.publish_datetime || ''}
                    onChange={e => setFormData({ ...formData, schedule: { ...formData.schedule, scheduled: true, publish_datetime: e.target.value } })}
                    className="bg-muted border-input h-8 text-sm" data-testid="mp-news-schedule-datetime" />
                  <Select value={formData.schedule?.timezone || 'UTC'}
                    onValueChange={v => setFormData({ ...formData, schedule: { ...formData.schedule, timezone: v } })}>
                    <SelectTrigger className="bg-muted border-input h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Live Preview */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-muted-foreground" />
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Live Preview</Label>
            </div>
            <div className="bg-muted/30 rounded-lg border border-input overflow-hidden">
              {formData.platform ? (
                <div className="bg-white dark:bg-gray-900">
                  {/* Platform header bar */}
                  <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700" style={{ backgroundColor: platformColor }}>
                    <div className="w-6 h-6 rounded flex items-center justify-center bg-white/20 text-white text-[10px] font-bold flex-shrink-0">
                      {(platformObj?.display_name || formData.platform)?.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-xs font-semibold text-white uppercase tracking-wide truncate">
                      {platformObj?.display_name || formData.platform}
                    </span>
                  </div>

                  {/* Hero image */}
                  {previewMedia?.length > 0 && (
                    <div className="w-full h-48 bg-gray-100 dark:bg-gray-800 overflow-hidden">
                      <img src={resolveUrl(previewMedia[0])} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}

                  {/* Article content */}
                  <div className="p-4 space-y-3">
                    {formData.category && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ color: platformColor, backgroundColor: platformColor + '15' }}>
                        {formData.category}
                      </span>
                    )}

                    {formData.title ? (
                      <h2 className="font-bold text-lg text-gray-900 dark:text-white leading-tight">{formData.title}</h2>
                    ) : (
                      <h2 className="font-bold text-lg text-gray-300 dark:text-gray-600 leading-tight italic">Headline will appear here...</h2>
                    )}

                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {formData.author?.name && <span className="font-medium text-gray-700 dark:text-gray-300">By {formData.author.name}</span>}
                      {formData.author?.name && formData.location && <span>·</span>}
                      {formData.location && <span>{formData.location}</span>}
                      <span>· Just now</span>
                    </div>

                    <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
                      {contentBlocks.map((block, i) => (
                        <div key={i} className="mb-3">
                          {block.type === 'text' ? (
                            block.content ? (
                              <p className={`text-sm text-gray-700 dark:text-gray-300 leading-relaxed ${i === 0 ? 'first-letter:text-2xl first-letter:font-bold first-letter:float-left first-letter:mr-1' : ''}`}>
                                {block.content}
                              </p>
                            ) : i === 0 ? (
                              <p className="text-sm text-gray-300 dark:text-gray-600 italic">Article body will appear here...</p>
                            ) : null
                          ) : block.url ? (
                            <figure className="my-2">
                              <img src={resolveUrl(block.url)} alt="" className="w-full rounded object-cover max-h-48" />
                              {block.caption && <figcaption className="text-[11px] text-gray-500 mt-1 italic">{block.caption}</figcaption>}
                            </figure>
                          ) : null}
                        </div>
                      ))}
                    </div>

                    {/* Inline images beyond the hero */}
                    {previewMedia?.length > 1 && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {previewMedia.slice(1).map((url, i) => (
                          <img key={i} src={resolveUrl(url)} alt="" className="w-full h-24 object-cover rounded" />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-gray-500 text-xs">
                    <span>&copy; {platformObj?.display_name || formData.platform} {new Date().getFullYear()}</span>
                    <span className="text-[10px]">PREVIEW</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-16 text-muted-foreground">
                  <p className="text-sm">Select a platform to see preview</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-border pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-input text-muted-foreground hover:bg-muted">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-foreground" data-testid="mp-news-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewsArticleFormDialog;
